import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, ArrowRight } from 'lucide-react';
import { formatApiError, getIntegrationInstallations, getIntegrationRegistry } from '../lib/api';
import { getIntegrationMeta } from '../integrations/catalog';

const statusLabel = (installation) => {
  if (!installation) return 'Available';
  if (installation.health_status === 'healthy' || installation.health_status === 'active') return 'Connected';
  if (installation.health_status === 'error') return 'Needs attention';
  return 'Installed';
};

const IntegrationsPage = ({ embedded = false } = {}) => {
  const [registry, setRegistry] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [registryRes, installsRes] = await Promise.all([
          getIntegrationRegistry(),
          getIntegrationInstallations(),
        ]);
        setRegistry(registryRes.data || []);
        setInstallations(installsRes.data || []);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const installedByIntegrationId = useMemo(() => {
    const map = new Map();
    installations.forEach((installation) => map.set(installation.integration_id, installation));
    return map;
  }, [installations]);

  const content = (
    <div className={embedded ? 'space-y-6' : 'p-8 space-y-6'} data-testid="integrations-page">
      <div>
        {embedded ? (
          <>
            <h2 className="text-2xl font-semibold tracking-tight font-['Outfit']">Integrations</h2>
            <p className="text-muted-foreground mt-1">Choose an integration to connect, configure, or test.</p>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Integrations</h1>
            <p className="text-muted-foreground mt-1">Connect the services Apollo can use across your organization.</p>
          </>
        )}
      </div>

      {error ? <div className="apollo-card p-4 text-sm text-red-400">{error}</div> : null}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {registry.map((integration) => {
            const installation = installedByIntegrationId.get(integration.integration_id);
            const meta = getIntegrationMeta(integration.integration_id);
            const Icon = meta.icon;
            return (
              <Card key={integration.integration_id} className="apollo-card overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className={`w-12 h-12 rounded-xl border border-border/60 bg-background/70 flex items-center justify-center ${meta.accent}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <Badge variant="outline" className={meta.badgeClass}>{statusLabel(installation)}</Badge>
                  </div>
                  <CardTitle className="pt-3 text-xl">{integration.display_name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground min-h-[40px]">{meta.shortDescription || integration.description}</p>
                  {installation ? (
                    <div className="text-xs text-muted-foreground">
                      Connected as <span className="text-foreground font-medium">{installation.display_name}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Not connected yet</div>
                  )}
                  <Button asChild className="w-full justify-between">
                    <Link to={`/integrations/${integration.integration_id}`}>
                      {installation ? 'Open integration' : 'Set up integration'}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default IntegrationsPage;
